/** The accounts API (web/server). Same origin, so the session cookie goes along and the CSP stays `connect-src 'self'`. */

export class AccountError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status = 0,
  ) {
    super(message);
    this.name = 'AccountError';
  }
}

const FALLBACK: Record<string, string> = {
  invalid_request: 'Something in that request was not accepted.',
  server_error: 'The server had a problem. Please try again.',
  forbidden: 'That request was refused.',
  signed_out: 'You are signed out.',
  not_found: 'That was not found.',
};

/**
 * How long to wait for the server. Past this it counts as unreachable, like being offline,
 * so a stalled network (a captive portal, a half-open connection) can't hang the app:
 * realm pages open offline for the account remembered on this device (access.ts).
 */
const TIMEOUT_MS = 10_000;

async function call<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api/${path}`, {
      method,
      signal: AbortSignal.timeout(TIMEOUT_MS),
      credentials: 'same-origin',
      headers: {
        ...(method === 'GET' ? {} : { 'x-realmery-request': '1' }),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new AccountError('offline', 'Could not reach Realmery. Check your connection.');
  }
  const type = response.headers.get('content-type') ?? '';
  if (!type.startsWith('application/json')) {
    // A static host with no Worker behind it (for example `vite preview` on its own).
    throw new AccountError('unavailable', 'Accounts are not available on this server.', response.status);
  }
  let json: T & { error?: string; message?: string };
  try {
    json = (await response.json()) as T & { error?: string; message?: string };
  } catch {
    // The connection dropped or timed out part-way through the answer.
    throw new AccountError('offline', 'Could not reach Realmery. Check your connection.');
  }
  if (!response.ok) {
    const code = json.error ?? 'server_error';
    const message = json.message && json.message !== code ? json.message : (FALLBACK[code] ?? 'That did not work.');
    throw new AccountError(code, message, response.status);
  }
  return json;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => call<T>('POST', path, body),
  del: <T>(path: string) => call<T>('DELETE', path),
};

export interface Wrap {
  kind: string;
  wrapped: string;
}

export interface Me {
  user: { id: string; email: string; deletionDueAt: number | null };
  hasPassword: boolean;
  passkeys: number;
  recentAuthUntil: number;
}

export interface SessionRow {
  id: string;
  label: string;
  createdAt: number;
  lastSeenAt: number;
  current: boolean;
}

export interface ActivityRow {
  type: string;
  at: number;
  /** The client's network (/24 or /48), not its exact address. */
  network: string;
}

export interface PasskeyRow {
  id: string;
  name: string;
  /** Who made the passkey, such as "Google Password Manager"; null when unknown. */
  provider: string | null;
  unlocks: boolean;
  synced: boolean;
  createdAt: number;
  lastUsedAt: number | null;
}
